import {AttrsSchema} from 'panel';

import {ButtonLayout, ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import type {TooltipAttrs} from 'common/components/tooltip/types';
import {panel2react} from 'common/util/react';

import {BaseButtonAttrs, baseButtonParams, ButtonBase} from '../base/button';
import {template} from './template';
import css from './index.inline.styl';

export interface ButtonJsxProps {
  attrs?: ButtonAttrs;
  on?: {
    click?: (ev: MouseEvent) => void;
  };
}

type Attrs = {
  class?: string;
  'highlight-icon'?: boolean;
  icon?: string;
  size?: ButtonSize;
  theme?: ButtonTheme;
  type?: ButtonType;
  pressed?: boolean;
  tooltip?: TooltipAttrs;
  layout?: ButtonLayout;
  'experimental-positioning'?: boolean;
};

export type ButtonAttrs = BaseButtonAttrs & Attrs;

export const buttonParams = {
  ...baseButtonParams,
  class: String,
  'highlight-icon': Boolean,
  icon: String,
  size: String,
  theme: String,
  type: String,
  pressed: Boolean,
  tooltip: Object,
  layout: String,
};

export class Button extends ButtonBase<Attrs> {
  static get attrsSchema(): AttrsSchema<ButtonAttrs> {
    return {
      ...super.attrsSchema,
      class: `string`,
      'highlight-icon': `boolean`,
      icon: `string`,
      size: {type: `string`, enum: Object.values(ButtonSize), default: ButtonSize.Medium},
      theme: {type: `string`, enum: Object.values(ButtonTheme), default: ButtonTheme.Secondary},
      type: {type: `string`, enum: Object.values(ButtonType), default: ButtonType.Info},
      layout: {type: `string`, enum: Object.values(ButtonLayout), default: ButtonLayout.Horizontal},
      pressed: `boolean`,
      'experimental-positioning': `boolean`,
      tooltip: `json`,
    };
  }

  get config() {
    return Object.assign(super.config, {
      css,
      template,
      helpers: {
        ...super.config.helpers,
        ButtonType,
      },
    });
  }

  handleClick = (ev: MouseEvent) => {
    if (this.attr(`disabled`)) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }
  };
}

export const ButtonEl = defineAndMakeJsxFactory<ButtonJsxProps>(`mp-button`, Button);
export const ButtonReact = panel2react<ButtonJsxProps>(`mp-button`, Button);
